import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Gift, Trophy, Star, Copy, Sparkles, AlertTriangle, ShieldCheck, CheckCircle2 } from 'lucide-react';

interface TuesdayShieldModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubscribe: () => void;
    onGoToProfile?: () => void;
    userGeneration: 'OLD' | 'NEW';
}

export const TuesdayShieldModal: React.FC<TuesdayShieldModalProps> = ({ 
    isOpen, 
    onClose, 
    onSubscribe,
    onGoToProfile,
    userGeneration
}) => {
    const [step, setStep] = useState(1);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    if (!isOpen) return null;

    const handleSkipForever = () => {
        localStorage.setItem('driver_score_tuesday_tour_disabled', 'true');
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-slate-700/80 w-full max-w-md rounded-3xl overflow-hidden shadow-2xl relative animate-in slide-in-from-bottom-8 duration-300">
                
                {/* Header Pattern */}
                <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-slate-950 p-6 pb-8 relative border-b border-indigo-500/20">
                    <div className="absolute top-0 right-0 p-4 z-20">
                        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors bg-slate-800/50 hover:bg-slate-800 p-1.5 rounded-full border border-slate-700/50">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(79,70,229,0.3)] relative">
                            {step === 1 && <Sparkles size={32} className="text-white animate-pulse" />}
                            {step === 2 && <Gift size={32} className="text-white animate-bounce" />}
                            {step === 3 && <Trophy size={32} className="text-white" />}
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white tracking-tight leading-tight">
                                {step === 1 && "🚀 Novidades do Driver SCORE!"}
                                {step === 2 && "🎁 Programa 'Zere seu Ano'"}
                                {step === 3 && "📲 Como Compartilhar & Ganhar"}
                            </h2>
                            <p className="text-[10px] text-indigo-400 font-bold uppercase tracking-widest mt-1">
                                Passo {step} de 3
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-5 bg-slate-900 max-h-[60vh] overflow-y-auto custom-scrollbar">
                    
                    {step === 1 && (
                        <div className="space-y-4 animate-in fade-in duration-300">
                            <p className="text-slate-300 text-xs leading-relaxed text-center">
                                Comandante, nosso ecossistema evoluiu! Trouxemos atualizações fantásticas para turbinar sua jornada:
                            </p>
                            <div className="space-y-2.5">
                                <div className="flex items-start gap-3 bg-slate-800/40 p-3 rounded-xl border border-slate-800">
                                    <Trophy className="text-yellow-500 shrink-0 w-4 h-4 mt-0.5" />
                                    <div>
                                        <h4 className="text-xs font-bold text-white">22 Níveis de Ranking</h4>
                                        <p className="text-[10px] text-slate-400 mt-0.5">Suba do Ferro até o nível lendário de 1M XP (**Singularidade**)!</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 bg-slate-800/40 p-3 rounded-xl border border-slate-800">
                                    <Sparkles className="text-orange-500 shrink-0 w-4 h-4 mt-0.5" />
                                    <div>
                                        <h4 className="text-xs font-bold text-white">Stories & Anúncios Livres</h4>
                                        <p className="text-[10px] text-slate-400 mt-0.5">Divulgue grátis no Marketplace a qualquer momento! Postar stories dá XP.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 bg-slate-800/40 p-3 rounded-xl border border-slate-800">
                                    <ShieldCheck className="text-emerald-500 shrink-0 w-4 h-4 mt-0.5" />
                                    <div>
                                        <h4 className="text-xs font-bold text-white font-medium">Turnos e Pausas Unlocked</h4>
                                        <p className="text-[10px] text-slate-400 mt-0.5">Fim definitivo dos limites diários de turnos e descansos no plano gratuito!</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 bg-red-950/20 p-3 rounded-xl border border-red-900/20">
                                    <AlertTriangle className="text-red-400 shrink-0 w-4 h-4 mt-0.5" />
                                    <div>
                                        <h4 className="text-xs font-bold text-red-300">Vigência de Assinatura</h4>
                                        <p className="text-[10px] text-red-400/90 mt-0.5">Novas contas precisam efetuar assinatura após a vigência sob risco de bloqueio. VIPs e Fundadores (R$ 10,90) continuam ininterruptos!</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4 animate-in fade-in duration-300">
                            <p className="text-slate-300 text-xs leading-relaxed text-center">
                                Quer jogar no <strong>modo Elite de graça</strong>? Apresentamos a promoção definitiva de indicações:
                            </p>
                            <div className="bg-gradient-to-br from-indigo-950/40 to-purple-950/20 border border-indigo-500/20 p-5 rounded-2xl text-center space-y-3">
                                <h3 className="text-sm font-black text-indigo-300 uppercase tracking-widest">Amigos Elite = Isenção Total</h3>
                                <p className="text-[11px] text-slate-300 leading-relaxed">
                                    Indique um colega de pista. <strong>Atenção:</strong> Esta promoção só é válida se <strong>você for assinante ativo</strong> e o <strong>indicado também assinar</strong>. Caso as duas condições sejam atendidas, <strong>ambos ganham 3 meses grátis!</strong>
                                </p>
                                <div className="h-px bg-slate-800 my-2" />
                                <p className="text-xs font-black text-emerald-400">
                                    🔥 Indique 4 colegas ativos e ZERE SEU ANO (12 meses grátis)!
                                </p>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4 animate-in fade-in duration-300">
                            <p className="text-slate-300 text-xs leading-relaxed text-center">
                                O processo é extremamente simples e rápido. Veja o passo a passo:
                            </p>
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-white">1</div>
                                    <p className="text-[11px] text-slate-300">Acesse a aba <strong>Meu Perfil</strong> no menu inferior.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-white">2</div>
                                    <p className="text-[11px] text-slate-300">Vá na seção <strong>Zere seu Ano</strong> e copie seu código exclusivo.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-bold text-white">3</div>
                                    <p className="text-[11px] text-slate-300">Envie aos seus amigos ou grupos pelo <strong>WhatsApp</strong>.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-6 h-6 rounded-full bg-emerald-950 border border-emerald-800 flex items-center justify-center text-xs font-bold text-emerald-400">4</div>
                                    <p className="text-[11px] text-emerald-300">Pronto! Com a assinatura deles ativa, a isenção cai para os dois.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation Dots */}
                    <div className="flex justify-center gap-1.5 py-2">
                        {[1, 2, 3].map(s => (
                            <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s === step ? 'w-5 bg-indigo-500' : 'w-1.5 bg-slate-700'}`} />
                        ))}
                    </div>

                    {/* Controls */}
                    <div className="space-y-3 pt-2 border-t border-slate-800">
                        <div className="flex gap-3">
                            {step > 1 && (
                                <button
                                    onClick={() => setStep(prev => prev - 1)}
                                    className="flex-1 bg-slate-800 hover:bg-slate-700 active:scale-[0.98] text-slate-300 font-bold py-3 px-4 rounded-xl border border-slate-700 transition-all flex items-center justify-center gap-1.5 text-xs uppercase"
                                >
                                    <ChevronLeft size={16} />
                                    Anterior
                                </button>
                            )}
                            {step < 3 ? (
                                <button
                                    onClick={() => setStep(prev => prev + 1)}
                                    className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 active:scale-[0.98] text-white font-bold py-3 px-4 rounded-xl shadow-lg shadow-indigo-900/20 transition-all flex items-center justify-center gap-1.5 text-xs uppercase"
                                >
                                    Próximo
                                    <ChevronRight size={16} />
                                </button>
                            ) : (
                                <button
                                    onClick={() => {
                                        onClose();
                                        onGoToProfile?.();
                                    }}
                                    className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 active:scale-[0.98] text-white font-bold py-3.5 px-4 rounded-xl shadow-lg shadow-emerald-900/20 transition-all flex items-center justify-center gap-2 text-xs uppercase tracking-wide"
                                >
                                    <CheckCircle2 size={16} />
                                    Ver Meu Código de Indicação
                                </button>
                            )}
                        </div>

                        {/* Flat Skip Options */}
                        <div className="flex flex-col items-center gap-1 pt-1">
                            <button
                                onClick={handleSkipForever}
                                className="text-slate-500 hover:text-slate-400 text-[10px] font-medium transition-colors"
                            >
                                Não exibir este aviso de terça-feira novamente
                            </button>
                            <button
                                onClick={onClose}
                                className="text-slate-600 hover:text-slate-500 text-[9px] transition-colors"
                            >
                                Fechar tour e ir para o Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
